// Custom API client - uses relative URLs to avoid mixed content issues
// The Emergent platform routes /api/* to the backend automatically

const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const makeRequest = async (method, path, data = null) => {
  // Use relative URL - this ensures same-origin request
  const url = `/api${path}`;
  
  const options = {
    method: method,
    headers: getHeaders(),
  };
  
  if (data && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(data);
  }
  
  try {
    const response = await fetch(url, options);
    
    // Handle redirects (307) by following them
    if (response.redirected) {
      console.log('Request was redirected to:', response.url);
    }
    
    const responseText = await response.text();
    let responseData;
    
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      responseData = responseText;
    }
    
    if (!response.ok) {
      throw { response: { status: response.status, data: responseData } };
    }
    
    return { data: responseData };
  } catch (error) {
    if (error.response) {
      throw error;
    }
    console.error('Network error:', error);
    throw { response: { status: 0, data: { detail: 'Ağ hatası oluştu' } } };
  }
};

const api = {
  get: (path, options = {}) => {
    let url = path;
    if (options.params) {
      const params = new URLSearchParams(options.params).toString();
      url += (path.includes('?') ? '&' : '?') + params;
    }
    return makeRequest('GET', url);
  },
  post: (path, data) => makeRequest('POST', path, data),
  put: (path, data) => makeRequest('PUT', path, data),
  delete: (path) => makeRequest('DELETE', path),
};

// Auth API - no trailing slashes
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  register: (username, password) => api.post('/auth/register', { username, password }),
  me: () => api.get('/auth/me'),
};

// News API
export const newsAPI = {
  getAll: (params) => api.get('/news/', { params }),
  count: (params) => api.get('/news/count', { params }),
  getById: (id) => api.get(`/news/${id}`),
  getRelated: (id) => api.get(`/news/${id}/related`),
  search: (q) => api.get('/news/search', { params: { q } }),
  getVizyonSektor: (params) => api.get('/news/vizyon-sektor', { params }),
  getVizyonSektorCount: () => api.get('/news/vizyon-sektor/count'),
  create: (data) => api.post('/news/', data),
  update: (id, data) => api.put(`/news/${id}`, data),
  delete: (id) => api.delete(`/news/${id}`),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
};

// Pages API (Hakkımızda, Reklam ve İş Birliği — editable static pages)
export const pagesAPI = {
  get: (slug) => api.get(`/pages/${slug}`),
  update: (slug, data) => api.put(`/pages/${slug}`, data),
};

// Inquiries API (Reklam ve İş Birliği talep formu)
export const inquiriesAPI = {
  create: (data) => api.post('/inquiries/', data),
  list: () => api.get('/inquiries/'),
  unreadCount: () => api.get('/inquiries/unread-count'),
  update: (id, data) => api.put(`/inquiries/${id}`, data),
  delete: (id) => api.delete(`/inquiries/${id}`),
};

// Site Settings API
export const settingsAPI = {
  get: () => api.get('/settings/'),
  update: (data) => api.put('/settings/', data),
  deleteAsset: (kind) => api.delete(`/settings/upload/${kind}`),
  uploadAsset: async (kind, file) => {
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    const resp = await fetch(`/api/settings/upload/${kind}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const text = await resp.text();
    let data;
    try { data = JSON.parse(text); } catch { data = text; }
    if (!resp.ok) throw { response: { status: resp.status, data } };
    return { data };
  },
};

// Ads API
export const adsAPI = {
  getAll: () => api.get('/ads/'),
  getActive: () => api.get('/ads/active'),
  update: (slotKey, data) => api.put(`/ads/${slotKey}`, data),
  deleteImage: (slotKey) => api.delete(`/ads/${slotKey}/image`),
  // Image upload — uses FormData; needs special handling (no JSON header)
  uploadImage: async (slotKey, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`/api/ads/${slotKey}/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { data = text; }
    if (!response.ok) {
      throw { response: { status: response.status, data } };
    }
    return { data };
  },
};

// Ticker API
export const tickerAPI = {
  getData: () => api.get('/ticker/'),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
